import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls/Button/Button";
import {
  Text,
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { RestrictionInfoBarStack } from "@whoosh/features/src/Restrictions/components/RestrictionInfoBarStack/RestrictionInfoBarStack";
import { RestrictionFeature } from "@whoosh/features/src/Restrictions/types";

import { getPunchPassSummaryAtom } from "../../../../atoms/selectors/getPunchPassSummaryAtom/getPunchPassSummaryAtom";
import { PunchPassSummary } from "../../../../atoms/types";
import { getBookingPlayerNoun } from "../../../../utils/getBookingPlayerNoun";
import { applyPunchPassModalAtom } from "../../atoms";
import { useGetLineItemsAndPriceForRound } from "../../hooks/useGetLineItemsAndPriceForRound";
import { RoundDetailsAnimationWrapper } from "./RoundDetailsAnimationWrapper";
import { RoundDiscountItem } from "./RoundDiscountItem";
import { RoundInfoDisplay } from "./RoundInfoDisplay";
import { RoundLineItem } from "./RoundLineItem";
import { Round_Round$key } from "./__generated__/Round_Round.graphql";

type Props = {
  roundRef: Round_Round$key | null;
  viewMore: boolean;
  hidePriceDetails?: boolean;
  roundRestrictions: RestrictionFeature.RestrictionBarData[] | null | undefined;
  setRoundIdToReplace: (roundId: string) => void;
  setIsEditBookingModalOpen: (isEditBookingModalOpen: boolean) => void;
  setViewMore: (value: boolean) => void;
  canEditBooking?: boolean;
};

export const Round = ({
  roundRef,
  viewMore,
  hidePriceDetails = false,
  roundRestrictions,
  setRoundIdToReplace,
  setIsEditBookingModalOpen,
  setViewMore,
  canEditBooking = true,
}: Props) => {
  const roundData = useFragment(RoundFragmentString, roundRef);
  const { lineItemGroupsForRound, totalPriceForRound } =
    useGetLineItemsAndPriceForRound({
      fragmentRef: roundData ?? null,
      roundId: roundData?.id as string,
    });
  const punchPassSummary: PunchPassSummary | null = useAtomValue(
    getPunchPassSummaryAtom(roundData?.id ?? null)
  );

  const hasRatePunchPassApplied =
    punchPassSummary?.hasRatePunchPassApplied ?? false;
  const hasTransportationPunchPassApplied =
    punchPassSummary?.hasTransportationPunchPassApplied ?? false;

  const availableRatePunchPasses =
    punchPassSummary?.availableRatePunchPasses ?? [];
  const availableTransportationPunchPasses =
    punchPassSummary?.availableTransportationPunchPasses ?? [];

  const validPunchPassCount = punchPassSummary?.validPunchPassCount ?? 0;
  const invalidPunchPassCount = punchPassSummary?.invalidPunchPassCount ?? 0;
  const totalPunchPassCount = punchPassSummary?.totalPunchPassCount ?? 0;
  const hasValidPunchPass = punchPassSummary?.hasValidPunchPass ?? false;

  const punchPassesAppliedToRoundCount =
    Number(hasRatePunchPassApplied) + Number(hasTransportationPunchPassApplied);

  const hasAvailablePunchPasses =
    availableRatePunchPasses.length > 0 ||
    availableTransportationPunchPasses.length > 0;

  const punchPassText =
    punchPassesAppliedToRoundCount > 0
      ? t`${punchPassesAppliedToRoundCount} Applied`
      : validPunchPassCount > 0
      ? t`${validPunchPassCount} Available`
      : invalidPunchPassCount > 0
      ? t`${invalidPunchPassCount} Unavailable`
      : "";

  const [applyPunchPassModal, setApplyPunchPassModal] = useAtom(
    applyPunchPassModalAtom
  );
  const playerNoun = getBookingPlayerNoun({
    bookingType: roundData?.booking?.__typename,
    copyOverrides:
      roundData?.booking?.startSlot?.agenda?.facility?.copyOverrides,
  });

  const toggleApplyPunchPassModal = () => {
    if (applyPunchPassModal) {
      // Closing modal: set to null
      setApplyPunchPassModal(null);
    } else {
      // Opening modal: set context with IDs
      if (roundData) {
        const roundId = roundData.id;
        const playerName =
          roundData.player?.fullName ?? `${t`New`} ${playerNoun.singular}`;
        setApplyPunchPassModal({
          type: "round",
          roundId,
          playerName,
        });
      }
    }
  };
  return (
    <Box gap={1} paddingVertical={2} testID="round-container">
      <RoundInfoDisplay
        roundRef={roundData ?? null}
        totalPriceForRound={totalPriceForRound ?? null}
        hideRoundPrice={hidePriceDetails}
        setRoundIdToReplace={setRoundIdToReplace}
        setIsEditBookingModalOpen={setIsEditBookingModalOpen}
        canEditBooking={canEditBooking}
        hasRatePunchPassApplied={hasRatePunchPassApplied}
        hasTransportationPunchPassApplied={hasTransportationPunchPassApplied}
        hasAvailableRatePunchPasses={availableRatePunchPasses.length > 0}
        hasAvailableTransportationPunchPasses={
          availableTransportationPunchPasses.length > 0
        }
        onClick={
          !hidePriceDetails && hasAvailablePunchPasses
            ? () => {
                setViewMore(!viewMore);
              }
            : undefined
        }
      />
      {roundRestrictions?.length && roundRestrictions?.length > 0 ? (
        <Box paddingVertical={2}>
          <RestrictionInfoBarStack
            restrictions={roundRestrictions ?? []}
            restrictionContext="Patron"
          />
        </Box>
      ) : null}
      <RoundDetailsAnimationWrapper
        enterFrom="bottom"
        hide={hidePriceDetails || !viewMore}
      >
        <VStack>
          {lineItemGroupsForRound?.map((lineItemGroup) => (
            <VStack key={lineItemGroup.id}>
              {lineItemGroup.lineItems.map((lineItem, idx) => (
                <RoundLineItem
                  key={lineItem.id ?? idx}
                  idx={idx}
                  lineItem={lineItem}
                />
              ))}
              {lineItemGroup.discounts.map((discount) => (
                <RoundDiscountItem key={discount.id} discount={discount} />
              ))}
            </VStack>
          ))}
          {totalPunchPassCount > 0 ? (
            <VStack
              marginLeft={9}
              marginTop={2}
              borderTopWidth={1}
              borderTopColor="surface-dividers"
              paddingTop={2}
            >
              <HStack
                alignItems="center"
                gap={1}
                justifyContent="space-between"
              >
                <HStack alignItems="center" gap={1}>
                  <Text
                    variant="mainBaseMedium"
                    color="text-primary"
                    lineHeight={32}
                  >
                    {t`Punch Passes:`}
                  </Text>
                  <Text
                    variant="mainBaseMedium"
                    color={
                      hasValidPunchPass ? "text-success" : "text-secondary"
                    }
                    lineHeight={32}
                  >
                    {punchPassText}
                  </Text>
                </HStack>
                {canEditBooking ? (
                  <Button
                    testID="edit-punch-pass-button"
                    accessibilityLabel={t`Edit punch pass`}
                    variant="secondary"
                    size="tiny"
                    color="text-primary"
                    onPress={toggleApplyPunchPassModal}
                  >
                    {t`Edit`}
                  </Button>
                ) : null}
              </HStack>
            </VStack>
          ) : null}
        </VStack>
      </RoundDetailsAnimationWrapper>
      {roundData?.nonPlayingHost ? (
        <Box
          width="100%"
          height={1}
          backgroundColor="surface-dividers"
          marginVertical={2}
        />
      ) : null}
    </Box>
  );
};

const RoundFragmentString = graphql`
  fragment Round_Round on Round {
    id
    player {
      fullName
    }
    booking {
      __typename
      startSlot {
        agenda {
          facility {
            copyOverrides {
              lessonPlayer {
                singular
              }
              bookingPlayer {
                singular
              }
            }
          }
        }
      }
    }
    ...RoundInfoDisplay_Round
    ...useGetLineItemsAndPriceForRound_Round
    ... on MemberRound {
      nonPlayingHost
    }
  }
`;
